import { useTranslation } from 'react-i18next'
import { Check, X, type LucideIcon } from 'lucide-react'

import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'

/**
 * How a chip reads: plainly, as one of several alternatives, as something
 * every match must carry, or as something ruled out.
 */
export type ChipTone = 'neutral' | 'include' | 'required' | 'exclude'

export interface Chip {
  label: string
  tone?: ChipTone
}

/** The badge each tone is drawn as — shared with anything else showing values. */
export const CHIP_VARIANTS: Record<ChipTone, 'gray' | 'success' | 'blue' | 'red'> = {
  neutral: 'gray',
  include: 'success',
  required: 'blue',
  exclude: 'red',
}

export interface ChipRowProps {
  /** Label down the left, e.g. "Locations". */
  label: string
  /** Shown before the label. */
  icon?: LucideIcon
  chips: Chip[]
  /** How many chips to show before the rest are counted into a "+n more". */
  max?: number
  /** Rendered after the chips, e.g. a badge saying where the values came from. */
  children?: React.ReactNode
  /** Puts the label above the chips instead of beside them — for narrow columns. */
  stacked?: boolean
  className?: string
}

/** One labelled row of chips — a compact way to summarise a list of values. */
export function ChipRow({
  label,
  icon: Icon,
  chips,
  max = 6,
  children,
  stacked,
  className,
}: ChipRowProps) {
  const { t } = useTranslation()
  const shown = chips.slice(0, max)
  const hidden = chips.length - shown.length

  return (
    <div
      className={cn('flex flex-col gap-1.5 py-2.5', !stacked && 'sm:flex-row sm:gap-4', className)}
    >
      <span
        className={cn(
          'flex shrink-0 items-center gap-2 pt-0.5 text-sm font-medium',
          !stacked && 'sm:w-40'
        )}
      >
        {Icon && <Icon className="size-4 shrink-0 text-muted-foreground" />}
        {label}
      </span>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
        {shown.map((chip, index) => {
          const tone = chip.tone ?? 'neutral'
          return (
            <Badge
              key={`${chip.label}-${index}`}
              variant={CHIP_VARIANTS[tone]}
              className="font-normal"
            >
              {tone === 'include' && <Check className="size-3" />}
              {tone === 'exclude' && <X className="size-3" />}
              {chip.label}
            </Badge>
          )
        })}
        {hidden > 0 && (
          <Badge variant="gray" className="font-normal">
            {t('common.andMoreCount', { count: hidden })}
          </Badge>
        )}
        {children}
      </div>
    </div>
  )
}
